import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Ship, MapPinned, Boxes, Users, ScrollText,
  ListChecks, History, CalendarRange, Sparkles, Scale, CircleHelp, X,
} from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { useAppAuth } from "@/auth/AppAuthProvider";
import { useTerminal } from "@/context/TerminalContext";

const navGroups = [
  { label: "Terminal", items: [
    { to: "/", label: "Overview", icon: LayoutDashboard, tid: TESTIDS.navDashboard, exact: true },
    { to: "/terminals", label: "Berths & Terminal", icon: MapPinned, tid: TESTIDS.navTerminals },
    { to: "/vessels", label: "Vessels", icon: Ship, tid: TESTIDS.navVessels },
    { to: "/port-calls", label: "Port Calls", icon: ListChecks, tid: TESTIDS.navPortCalls },
    { to: "/timeline", label: "Schedule", icon: CalendarRange, tid: "nav-timeline" },
  ] },
  { label: "Operations", items: [
    { to: "/assets", label: "Terminal Assets", icon: Boxes, tid: TESTIDS.navAssets },
    { to: "/personnel", label: "Labour & Personnel", icon: Users, tid: TESTIDS.navPersonnel, capability: "case.confidential.read" },
    { to: "/sops", label: "SOP & Rules", icon: ScrollText, tid: TESTIDS.navSops },
    { to: "/labor-knowledge", label: "Labour Knowledge", icon: Scale, tid: "nav-labor-knowledge" },
  ] },
  { label: "Review", items: [
    { to: "/vision", label: "AI Insights", icon: Sparkles, tid: "nav-vision", capability: "map.manage" },
    { to: "/audit", label: "Activity & Approvals", icon: History, tid: TESTIDS.navAudit, capability: "audit.read" },
    { to: "/help", label: "Help & Playbooks", icon: CircleHelp, tid: TESTIDS.navHelp },
  ] },
];

export default function Sidebar({ mobile = false, onClose = () => {} }) {
  const { hasCapability } = useAppAuth();
  const { terminals, currentTerminalId, setCurrentTerminalId, loading } = useTerminal();
  const loc = useLocation();
  const isActive = item => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  return (
    <aside className={`app-sidebar ${mobile ? "app-sidebar-mobile" : "app-sidebar-desktop"}`} aria-label="Primary navigation">
      <div className="app-brand">
        <div className="app-brand-mark" aria-hidden="true">PHQ</div>
        <div><strong>Portcall HQ</strong><span>Terminal Operations</span></div>
        {mobile && <button type="button" className="app-icon-button ml-auto" onClick={onClose} aria-label="Close navigation"><X size={18} /></button>}
      </div>
      <label className="terminal-context-card">
        <span>Current terminal</span>
        <select value={currentTerminalId || ""} onChange={event => setCurrentTerminalId(event.target.value)} disabled={loading || terminals.length === 0} data-testid="global-terminal-selector" aria-label="Current terminal">
          {terminals.length === 0 && <option value="">No terminal configured</option>}
          {terminals.map(terminal => <option key={terminal.id} value={terminal.id}>{terminal.name}</option>)}
        </select>
      </label>
      <nav className="app-nav">
        {navGroups.map(group => <div className="app-nav-group" key={group.label}>
          <div className="app-nav-label">{group.label}</div>
          {group.items.filter(item => !item.capability || hasCapability(item.capability)).map(item => {
            const Icon = item.icon;
            const active = isActive(item);
            return <Link key={item.to} to={item.to} data-testid={item.tid} aria-current={active ? "page" : undefined} className={`app-nav-link ${active ? "active" : ""}`} onClick={onClose}>
              <Icon size={16} strokeWidth={1.7} /><span>{item.label}</span>
            </Link>;
          })}
        </div>)}
      </nav>
      <p className="app-governance-note">AI output is advisory. Deterministic checks and final operational approval remain separate human-governed responsibilities.</p>
    </aside>
  );
}
